import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryContextSettingID } from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { XYChartAxis } from 'src/app/utils/echarts/xy-chart-axis';
import { BiVizDashboardUsersActivity } from '../../mopa-users/sql-maps/biviz-dashboard-users-activity-datasource-sql-map';
import { BIVizDashboardAdEvents } from '../sql-maps/biviz-ad-events-datasource-sql-map';

export interface AdsDauDeuDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {}

/** This class generates DataLoader instance for analytics vs store reports differences */
export class AdsDauDeuDataLoaderFactory extends Factory<
    DataLoader,
    AdsDauDeuDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'AdsDauDeuDataLoaderFactory';

    getInstance(options: AdsDauDeuDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const adEventsDaily = BIVizDashboardAdEvents;

                // identify columns not supported in datasource
                const adEventsDailyDsColumns =
                    DataLoader.separatePresentAndMissingDims(
                        queryContext.datasourceColumns,
                        adEventsDaily.sqlMap.columns
                    );

                const adEventsDailyDsColumnsStatus =
                    DataLoader.getMissingColumnsStatus(
                        adEventsDailyDsColumns.missing
                    );

                const adEventsDailySQL = adEventsDaily.compileSQLStatements(
                    adEventsDailyDsColumns.present,
                    queryContext.settings,
                    options
                    // selectedDayNoSQL
                );

                const usersdashActivity = BiVizDashboardUsersActivity;

                // identify columns not supported in datasource
                const usersdashActivityDsColumns =
                    DataLoader.separatePresentAndMissingDims(
                        queryContext.datasourceColumns,
                        usersdashActivity.sqlMap.columns
                    );

                const usersdashActivityDsColumnsStatus =
                    DataLoader.getMissingColumnsStatus(
                        usersdashActivityDsColumns.missing
                    );

                const usersdashActivitySQL =
                    usersdashActivity.compileSQLStatements(
                        usersdashActivityDsColumns.present,
                        queryContext.settings,
                        options
                        // selectedDayNoSQL
                    );
                // const selectFacts = is_in_context_app_version
                //     ? 'ds.date, ds.proper_id, app_version.*,'
                //     : 'ds.*,';

                const query = `
-- DataLoaderId: ${this.instanceId}
WITH adsFlowBase AS
(
  SELECT 
    ds.*,
    ${adEventsDailySQL.splitSelect}
    ${adEventsDailySQL.adsFlowSplitSelect}
    ${adEventsDailySQL.cohortSplitSelect}
  FROM \`${generateTableFullPath(
      options.gcpProjectId,
      adEventsDaily.sqlMap.dataset,
      adEventsDaily.sqlMap.tableId
  )}\` ds
  ${adEventsDailySQL.join}
  ${adEventsDailySQL.where}
)

, adEventsDaily AS
(
  SELECT 
    ${adEventsDailySQL.splitGroupBy}${adEventsDailySQL.dateTrunc} AS date
    , SUM(impressions) AS impressions
    , SUM(revenue_pln) AS revenue_pln
    , SUM(revenue_usd) AS revenue_usd

  FROM
  (
    SELECT ${adEventsDailySQL.splitGroupBy}date
    , SUM(ad_show_starts) AS impressions
    , SUM(revenue_net_pln) AS revenue_pln
    , SUM(revenue_net_usd) AS revenue_usd

      FROM adsFlowBase
      GROUP BY ${adEventsDailySQL.splitGroupBy}date 
  )
  GROUP BY ${adEventsDailySQL.splitGroupBy}date 
)

, engagedUsers AS
(
  SELECT  
    ${adEventsDailySQL.splitSelectConcatCohortAdsFlow}
    date
    , engaged_users
  FROM
  (
    SELECT 
      ${adEventsDailySQL.cohortSplitGroupBy}
      ${adEventsDailySQL.dateTrunc} AS date
      , SUM(engaged_users) AS engaged_users
    FROM
    (
      SELECT 
        ${adEventsDailySQL.cohortSplitSelect}date
        , COUNT(DISTINCT IF(ad_rewards > 0, player_id, NULL)) AS engaged_users
      FROM \`${generateTableFullPath(
          options.gcpProjectId,
          adEventsDaily.sqlMap.dataset,
          adEventsDaily.sqlMap.tableId
      )}\` ds
          ${adEventsDailySQL.join}
          ${adEventsDailySQL.cohortWhere}  
      GROUP BY ${adEventsDailySQL.cohortSplitGroupBy}date 
    )
    GROUP BY ${adEventsDailySQL.cohortSplitGroupBy}date
  ) 
  ${
      adEventsDailySQL.is_in_context_split_ads_flow_specific_columns
          ? `CROSS JOIN (SELECT DISTINCT ads_flow_split_group FROM adsFlowBase)`
          : ''
  }
)

, activeUsers AS
(
  SELECT
    ${adEventsDailySQL.splitSelectConcatCohortAdsFlow}
    date
    , active_users  
  FROM (  
    SELECT 
      ${adEventsDailySQL.cohortSplitGroupBy}${
          adEventsDailySQL.dateTrunc
      } AS date
      , SUM(active_users) AS active_users
    FROM
    (
      SELECT 
        
        ${
            usersdashActivitySQL.splitGroupBy != ''
                ? `${usersdashActivitySQL.splitSelect.replace(
                      'AS split_group',
                      'AS cohort_split_group'
                  )}`
                : ''
        }date
        , COUNT(DISTINCT player_id) AS active_users
      FROM \`${generateTableFullPath(
          options.gcpProjectId,
          usersdashActivity.sqlMap.dataset,
          usersdashActivity.sqlMap.tableId
      )}\` ds
          ${usersdashActivitySQL.join}
          ${usersdashActivitySQL.where}  
      GROUP BY ${adEventsDailySQL.cohortSplitGroupBy}date 
    )
    GROUP BY ${adEventsDailySQL.cohortSplitGroupBy}date 
  )
  ${
      adEventsDailySQL.is_in_context_split_ads_flow_specific_columns
          ? `CROSS JOIN (SELECT DISTINCT ads_flow_split_group FROM adsFlowBase)`
          : ''
  }
)

SELECT
${
    adEventsDailySQL.splitGroupBy !== ''
        ? `adEventsDaily.split_group AS split_group, `
        : ''
}adEventsDaily.date AS date
  , activeUsers.active_users
  , engagedUsers.engaged_users
  , ROUND(SAFE_DIVIDE(engagedUsers.engaged_users, activeUsers.active_users), 4) AS engagement_rate
  , ROUND(SAFE_DIVIDE(adEventsDaily.impressions, engagedUsers.engaged_users), 2) AS impressions_per_deu
  , ROUND(adEventsDaily.revenue_pln, 2) AS revenue_pln
  , ROUND(adEventsDaily.revenue_usd, 2) AS revenue_usd
  , ROUND(SAFE_DIVIDE(adEventsDaily.revenue_pln, activeUsers.active_users), 3) AS arpdau_pln
  , ROUND(SAFE_DIVIDE(adEventsDaily.revenue_usd, activeUsers.active_users), 3) AS arpdau_usd
  , ROUND(SAFE_DIVIDE(adEventsDaily.revenue_pln, engagedUsers.engaged_users), 3) AS arpdeu_pln
  , ROUND(SAFE_DIVIDE(adEventsDaily.revenue_usd, engagedUsers.engaged_users), 3) AS arpdeu_usd
FROM adEventsDaily
LEFT JOIN activeUsers
ON adEventsDaily.date = activeUsers.date
${
    adEventsDailySQL.splitGroupBy !== ''
        ? `AND adEventsDaily.split_group = activeUsers.split_group`
        : ''
}
LEFT JOIN engagedUsers
ON adEventsDaily.date = engagedUsers.date
${
    adEventsDailySQL.splitGroupBy !== ''
        ? `AND COALESCE(adEventsDaily.split_group, activeUsers.split_group) = engagedUsers.split_group`
        : ''
}
ORDER BY ${
                    adEventsDailySQL.splitGroupBy !== ''
                        ? `COALESCE(adEventsDaily.split_group, activeUsers.split_group, engagedUsers.split_group), `
                        : ''
                }adEventsDaily.date ASC
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                let data = [...queryResponse.result];
                this.latestData = data;
                bq2echartsformat(data);
                XYChartAxis.fillMissingTimeGapRows(
                    data,
                    queryContext,
                    'date',
                    queryContext.settings[
                        QueryContextSettingID.DATE_AGGREGATION
                    ] ?? 'day'
                );

                this.emitter.emit({
                    data: this.latestData,
                    queryContext: queryContext,
                    status: adEventsDailyDsColumnsStatus.pendingDataLoaderResultStatus,
                    statusMessage:
                        adEventsDailyDsColumnsStatus.pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
